import {
  getAcesByUser,
  getUser, updateAssocClient, updateUser,
} from "services/UserService"
import {getCurrentEnvironmentId} from "redux/environment.selector"
import RootModel from "redux/models/index"
import {createModel} from "@rematch/core"
import {ACES, UAC, UserDtoSummaryWithAces} from "components/admin/user/UserManager.types"
import {updateUserAces} from "./user.utils"
import {getUserFullName} from "../../commons/getUserFullName";

export interface ModelUser {
  "userWithAces": UserDtoSummaryWithAces | undefined,
  "userAssocClient": UAC | undefined,
}

const initialState: ModelUser = {
  userWithAces: undefined,
  userAssocClient: undefined
}

export default createModel<RootModel>()({
  state: initialState,
  reducers: {
    setAces(state: ModelUser, userWithAces: UserDtoSummaryWithAces) {
      return {
        ...state,
        userWithAces
      }
    },
    setUac(state: ModelUser, uac: UAC | undefined) {
      return {
        ...state,
        userAssocClient: uac
      }
    },
    'environment/cleanStates': () => {
      return initialState
    },
  },
  effects: (dispatch) => {
    return {
      async loadAssocClient(userId: string, state): Promise<UAC | undefined> {
        const environmentId = getCurrentEnvironmentId(state)
        const user = await getUser(userId, environmentId)
        const assocEnvironment = user.assocEnvironments.find(a => a.environment.id === environmentId)
        const uac: UAC | undefined = assocEnvironment ? {
          active: assocEnvironment.active,
          id: assocEnvironment.id,
          user: {
            ...user,
            fullName: getUserFullName(user.firstName, user.lastName),
          }
        } : undefined
        dispatch.user.setUac(uac)
        return uac
      },
      async loadAces({userId, assocClient, environmentId}: { userId: number, assocClient: UAC, environmentId: number }, state): Promise<any> {
        const aces = await getAcesByUser(userId, environmentId)
        return dispatch.user.setAces({
          id: assocClient.user.id,
          email: assocClient.user.email,
          firstName: assocClient.user.firstName,
          lastName: assocClient.user.lastName,
          aces
        })
      },
      async updateUser({id, data}: { id: any, data: any }, state): Promise<UAC | undefined> {
        const environmentId = getCurrentEnvironmentId(state)
        return updateUser(id, data, environmentId)
          .then(newData => {
            const uac: UAC | undefined = state.user.userAssocClient ? {
              ...state.user.userAssocClient,
              user: {
                ...newData,
                fullName: getUserFullName(newData.firstName, newData.lastName),
              }
            } : undefined
            dispatch.user.setUac(uac)
            return uac
          })
      },
      async updateUserAces({userId, aces}: { userId: any, aces: Pick<ACES, "target" | "roles">[] }, state): Promise<void> {
        const environmentId = getCurrentEnvironmentId(state)
        if (state.user.userWithAces) {
          const userWithAces = await updateUserAces({userId, aces}, state.user.userWithAces, environmentId)
          if (userWithAces) {
            dispatch.user.setAces(userWithAces)
          }
        }
      },
      async updateAssocClientActive({uac, active}: {
        forBatch?: boolean
        uac: { id: number | string },
        active: boolean
      }): Promise<any> {
        const newUac = await updateAssocClient(uac.id, {active})
        return dispatch.user.setUac({
          ...newUac,
          user: {
            ...newUac.user,
            fullName: getUserFullName(newUac.user.firstName, newUac.user.lastName),
          }
        })
      },
    }
  },
})
